import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setVisibleRecords } from '../../../../actions/ui';
import { getRecords } from '../../../../helpers/front-helper';
import { TablePagination } from '../shared/table/TablePagination';
import { RecordsTable } from './components/RecordsTable';

export const RecordsScreen = () => {
    const dispatch = useDispatch();
    const { token } = useSelector((state) => state.auth);

    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [records, setRecords] = React.useState([]);
    const [methodFilter, setMethodFilter] = React.useState('');
    const [functionFilter, setFunctionFilter] = React.useState('');
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [offset, setOffset] = React.useState(0);
    const [pagination, setPagination] = React.useState({
        total: 0,
        limit: 10,
        offset: 0,
        hasNext: false,
        hasPrev: false,
    });

    const [debouncedMethodFilter, setDebouncedMethodFilter] = React.useState('');
    const [debouncedFunctionFilter, setDebouncedFunctionFilter] = React.useState('');

    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedMethodFilter(methodFilter.trim());
        }, 600);

        return () => clearTimeout(timeoutId);
    }, [methodFilter]);

    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedFunctionFilter(functionFilter.trim());
        }, 600);

        return () => clearTimeout(timeoutId);
    }, [functionFilter]);

    React.useEffect(() => {
        const fetchRecords = async () => {
            if (!token) {
                setError('No se pudo validar la sesión.');
                setRecords([]);
                setPagination((previous) => ({
                    ...previous,
                    total: 0,
                    hasNext: false,
                    hasPrev: false,
                }));
                return;
            }

            setIsLoading(true);
            setError('');

            const resp = await getRecords(token, {
                limit: rowsPerPage,
                offset,
                ...(debouncedMethodFilter ? { method: debouncedMethodFilter } : {}),
                ...(debouncedFunctionFilter ? { function: debouncedFunctionFilter } : {}),
            });

            if (!resp?.success) {
                setError(resp?.message || 'No se pudieron obtener los records.');
                setRecords([]);
                setPagination((previous) => ({
                    ...previous,
                    total: 0,
                    hasNext: false,
                    hasPrev: false,
                }));
                setIsLoading(false);
                return;
            }

            const fetchedRecords = Array.isArray(resp.data) ? resp.data : [];
            const normalizedRecords = fetchedRecords.map((record) => ({
                ...record,
                method: record.module || '-',
                functionName: record.function || '-',
                userDisplay: record.user?.name || record.user?.email || '-',
            }));

            setRecords(normalizedRecords);
            setPagination({
                total: Number(resp.total) || 0,
                limit: Number(resp.limit) || rowsPerPage,
                offset: Number(resp.offset) || 0,
                hasNext: Boolean(resp.hasNext),
                hasPrev: Boolean(resp.hasPrev),
            });
            setIsLoading(false);
        };

        fetchRecords();
    }, [token, rowsPerPage, offset, debouncedMethodFilter, debouncedFunctionFilter]);

    React.useEffect(() => {
        dispatch(setVisibleRecords(records));
    }, [dispatch, records]);

    React.useEffect(() => {
        return () => {
            dispatch(setVisibleRecords([]));
        };
    }, [dispatch]);

    const formatDateTime = (value) => {
        if (!value) return '-';

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '-';

        return date.toLocaleString('es-MX', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    const handleMethodFilterChange = (value) => {
        setMethodFilter(value);
        setOffset(0);
    };

    const handleFunctionFilterChange = (value) => {
        setFunctionFilter(value);
        setOffset(0);
    };

    const handleRowsPerPageChange = (value) => {
        setRowsPerPage(Number(value));
        setOffset(0);
    };

    const handleClearFilters = () => {
        setMethodFilter('');
        setFunctionFilter('');
        setDebouncedMethodFilter('');
        setDebouncedFunctionFilter('');
        setOffset(0);
    };

    return (
        <div className="row">
            <div className="col-12">
                <div className="card stretch stretch-full card__no-border">
                    <div className="card-body pt-1">
                        <div className="card__stat border-0 mb-3">
                            <div className="card-body py-3">
                                <div className="row g-2 align-items-end">
                                    <div className="col-lg-3 col-md-6">
                                        <label className="form-label form-label-sm mb-1 fw-semibold">Método</label>
                                        <input
                                            type="search"
                                            className="form-control form-control-sm"
                                            placeholder="Filtrar por método..."
                                            value={methodFilter}
                                            onChange={({ target }) => handleMethodFilterChange(target.value)}
                                        />
                                    </div>

                                    <div className="col-lg-3 col-md-6">
                                        <label className="form-label form-label-sm mb-1 fw-semibold">Función</label>
                                        <input
                                            type="search"
                                            className="form-control form-control-sm"
                                            placeholder="Filtrar por función..."
                                            value={functionFilter}
                                            onChange={({ target }) => handleFunctionFilterChange(target.value)}
                                        />
                                    </div>

                                    <div className="col-lg-2 col-md-4">
                                        <label className="form-label form-label-sm mb-1 fw-semibold">Mostrar</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={rowsPerPage}
                                            onChange={({ target }) => handleRowsPerPageChange(target.value)}
                                        >
                                            {[10, 20, 50, 100].map((size) => (
                                                <option key={size} value={size}>{size}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-lg-2 col-md-4 d-grid gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary w-100"
                                            onClick={handleClearFilters}
                                        >
                                            Limpiar filtros
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                            <div className="text-muted fs-12">
                                {methodFilter.trim() || functionFilter.trim()
                                    ? `Resultados para Método: "${methodFilter.trim() || '-'}" / Función: "${functionFilter.trim() || '-'}"`
                                    : 'Listado general de records INFO'}
                            </div>
                            <div className="text-muted fs-12">Mostrando {records.length} de {pagination.total}</div>
                        </div>

                        {!isLoading && error && (
                            <div className="alert alert-danger py-2" role="alert">{error}</div>
                        )}

                        <RecordsTable
                            records={records}
                            isLoading={isLoading}
                            formatDateTime={formatDateTime}
                        />

                        <TablePagination
                            total={pagination.total}
                            limit={pagination.limit}
                            offset={pagination.offset}
                            hasNext={pagination.hasNext}
                            hasPrev={pagination.hasPrev}
                            onOffsetChange={setOffset}
                            isLoading={isLoading}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
